// src/pages/LexiconPage.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { auth, db } from '../firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, doc, getDocs, setDoc } from 'firebase/firestore';

const LexiconPage = () => {
  const [user, setUser] = useState(null);
  const [words, setWords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const navigate = useNavigate();
  
  // ✅ Your Merriam-Webster API Key
  const mwApiKey = 'e9a1fc93-d1d7-4654-9646-3962e12b16b7';

  // 1. Listen for authentication state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (!currentUser) {
        setWords([]);
        setIsLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // 2. Fetch the user's personal lexicon from Firestore
  useEffect(() => {
    if (user) {
      const fetchLexicon = async () => {
        setIsLoading(true);
        try {
          const lexiconColRef = collection(db, 'users', user.uid, 'lexicon');
          const snapshot = await getDocs(lexiconColRef);
          
          const wordsArray = snapshot.docs.map(doc => ({
            id: doc.id,
            term: doc.id,
            ...doc.data(),
          }));
          
          setWords(wordsArray);
        } catch (err) { 
          setError("Failed to fetch your lexicon."); 
          console.error(err); 
        }
        setIsLoading(false);
      };
      fetchLexicon();
    }
  }, [user]);

  // 3. Handle looking up a word using the Merriam-Webster API
  const handleLookup = async (e) => {
    e.preventDefault();
    const term = searchTerm.trim().toLowerCase();
    if (!term) return;

    setIsLookingUp(true);
    setLookupResult(null);
    setError('');

    try {
      const proxyUrl = 'https://corsproxy.io/?'; 
      const apiUrl = `https://www.dictionaryapi.com/api/v3/references/collegiate/json/${term}?key=${mwApiKey}`;
      
      const response = await fetch(proxyUrl + encodeURIComponent(apiUrl));
      
      if (!response.ok) {
        throw new Error('Word not found by API');
      }
      
      const data = await response.json();

      if (!data || data.length === 0 || typeof data[0] !== 'object') {
        throw new Error(`Word not found. Suggestions: ${data.join(', ')}`);
      }

      const entry = data[0];
      const definition = entry.shortdef ? entry.shortdef[0] : 'No definition available.';
      const headword = entry.hwi.hw.replace(/\*/g, '');

      setLookupResult({
        term: headword,
        definition: definition,
      });

    } catch (err) {
      setLookupResult(null);
      setError(`Sorry, "${searchTerm}" could not be found. ${err.message}`);
      console.error("Dictionary API Error:", err);
    }
    setIsLookingUp(false);
  };

  // 4. Handle SAVING the looked-up word to Firebase
  const handleSaveWord = async () => {
    if (!lookupResult || !user) return;
    
    const term = lookupResult.term.toLowerCase();
    const wordDocRef = doc(db, 'users', user.uid, 'lexicon', term);
    
    const newWordData = {
      definition: lookupResult.definition,
      status: 'New', 
      addedAt: new Date(),
    };

    try {
      await setDoc(wordDocRef, newWordData);
      setWords(prevWords => [...prevWords, { term: term, ...newWordData }]);
      setSearchTerm('');
      setLookupResult(null);
    } catch (err) {
      console.error("Firestore save error:", err);
      if (err.code === 'permission-denied') {
        setError("Permission denied. Please refresh and try again.");
      } else {
        setError("Failed to save the word. Please try again.");
      }
    }
  };
  
  // 5. Get color for status
  const getStatusColor = (status) => {
    switch (status) {
      case 'Mastered': return 'bg-green-500';
      case 'In Progress': return 'bg-yellow-500';
      case 'New': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="container mx-auto p-4 md:p-8">
      <header className="text-center mb-12">
        <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900">My Lexicon</h1>
        <p className="text-lg text-gray-600 mt-2">Your personal treasury of professional terms.</p>
      </header>

      {isLoading ? ( 
        <div className="text-center">Loading...</div> 
      ) : !user ? (
        <div className="text-center bg-white p-8 rounded-lg shadow-md max-w-md mx-auto">
          <h2 className="text-2xl font-bold mb-4">Please Sign In</h2>
          <p className="mb-6">Sign in to build your personal lexicon.</p>
          <Link to="/login" className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700">
            Go to Login Page
          </Link>
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Word Discovery Form */}
          <aside className="w-full lg:w-1/3 bg-white p-6 rounded-2xl shadow-lg h-fit">
            <h2 className="text-2xl font-bold mb-4">Discover a Word</h2>
            <form onSubmit={handleLookup} className="space-y-4">
              <div>
                <label htmlFor="searchTerm" className="block text-sm font-medium text-gray-700">
                  Enter a word (e.g., "test")
                </label>
                <div className="flex gap-2 mt-1">
                  <input 
                    type="text" 
                    id="searchTerm" 
                    value={searchTerm} 
                    onChange={e => setSearchTerm(e.target.value)} 
                    className="w-full p-2 border rounded-md" 
                    placeholder="e.g., knowledge"
                  />
                  <button 
                    type="submit" 
                    disabled={isLookingUp} 
                    className="px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:bg-blue-300"
                  >
                    {isLookingUp ? '...' : 'Look Up'}
                  </button>
                </div>
              </div>
            </form>

            {error && <p className="text-red-500 text-sm mt-4">{error}</p>}
            
            {lookupResult && (
              <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
                <h3 className="text-xl font-bold">{lookupResult.term}</h3>
                <p className="mt-2 text-gray-700">{lookupResult.definition}</p>
                <button 
                  onClick={handleSaveWord} 
                  className="w-full mt-4 px-4 py-2 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700"
                >
                  Save to My Lexicon
                </button>
              </div>
            )}
          </aside>

          {/* Word List Display */}
          <main className="w-full lg:w-2/3 bg-white p-6 rounded-2xl shadow-lg">
             <div className="flex justify-between items-center mb-4">
               <h2 className="text-2xl font-bold">Your Saved Words ({words.length})</h2>
               {words.length > 0 && (
                 <button 
                   onClick={() => navigate('/lexicon/practice')} 
                   className="px-4 py-2 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700"
                 >
                   Start Practice Session
                 </button>
               )}
             </div>
             {words.length > 0 ? (
                <ul className="space-y-3">
                  {words.map(word => (
                    <li key={word.id} className="p-4 rounded-lg border border-gray-200">
                      <div className="flex justify-between items-center">
                        <h3 className="text-xl font-bold text-gray-800">{word.term}</h3>
                        <span className={`px-3 py-1 text-xs text-white rounded-full ${getStatusColor(word.status)}`}>
                          {word.status}
                        </span>
                      </div>
                      <p className="text-gray-600 mt-2">{word.definition}</p>
                    </li>
                  ))}
                </ul>
             ) : ( 
               <p className="text-gray-500">Your lexicon is empty. Look up a word to get started!</p> 
             )}
          </main>
        </div>
      )}
    </div>
  );
};

export default LexiconPage;